import React, { useState, useMemo } from 'react';
import { Container, Box, Typography, TextField, InputAdornment, Card, CardContent, CardActionArea, CircularProgress, Alert, Chip, MenuItem, Select, FormControl, InputLabel, IconButton } from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSchemes } from '../hooks/useSchemes';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedTitle, getLocalizedDescription, isAvailableInState } from '../models/Scheme';

export const SchemesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const { data: schemes, isLoading, error } = useSchemes();

  // Categories list
  const categories = [
    'all',
    'farmer',
    'student',
    'women',
    'senior_citizen',
    'business',
    'health',
    'housing',
    'employment',
  ];

  // Extract available states from schemes
  const availableStates = useMemo(() => {
    if (!schemes || schemes.length === 0) return ['all'];
    
    const statesSet = new Set<string>(['all']);
    schemes.forEach(scheme => {
      // Ensure stateAvailability is an array
      if (!Array.isArray(scheme.stateAvailability) || scheme.stateAvailability.length === 0) {
        return;
      }
      
      // Check if scheme is available in all states
      const hasAll = scheme.stateAvailability.some(
        (state) => state && state.toLowerCase().trim() === 'all'
      );
      if (hasAll) {
        return; // Skip adding individual states for schemes available everywhere
      }
      
      // Normalize states to lowercase and add to set
      scheme.stateAvailability.forEach(state => {
        if (state && typeof state === 'string') {
          const normalized = state.toLowerCase().trim();
          if (normalized && normalized !== 'all') {
            statesSet.add(normalized);
          }
        }
      });
    });
    
    const statesList = Array.from(statesSet);
    // Sort alphabetically, except 'all' stays first
    statesList.sort((a, b) => {
      if (a === 'all') return -1;
      if (b === 'all') return 1;
      return a.localeCompare(b);
    });
    
    return statesList;
  }, [schemes]);

  // Get localized state name
  const getLocalizedStateName = (state: string): string => {
    const normalizedInput = state.toLowerCase().trim();
    
    if (normalizedInput === 'all') {
      return t('all_states');
    }
    
    // Normalize state name: replace spaces with underscores
    const normalizedState = normalizedInput.replace(/\s+/g, '_');
    const stateKey = `state_${normalizedState}`;
    
    // Check if translation exists
    const translated = t(stateKey);
    if (translated !== stateKey) {
      return translated;
    }
    
    // Fallback to capitalized original
    return normalizedInput
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const filteredSchemes = schemes?.filter(scheme => {
    // Category filter
    if (selectedCategory !== 'all' && scheme.category !== selectedCategory) {
      return false;
    }

    // State filter
    if (selectedState !== 'all' && !isAvailableInState(scheme, selectedState)) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const title = getLocalizedTitle(scheme, currentLanguage);
      const description = getLocalizedDescription(scheme, currentLanguage);
      const query = searchQuery.toLowerCase();
      if (!title.toLowerCase().includes(query) && !description.toLowerCase().includes(query)) {
        return false;
      }
    }

    return true;
  });

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedState('all');
    setSearchQuery('');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" color="primary" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
          {t('government_schemes')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('browse_active_schemes')}
        </Typography>
      </Box>
        
      <TextField
        fullWidth
        placeholder={t('search_schemes')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Filters */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-label">{t('category')}</InputLabel>
              <Select
                labelId="category-label"
                value={selectedCategory}
                label={t('category')}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {t(`category_${category}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="state-label">{t('state')}</InputLabel>
              <Select
                labelId="state-label"
                value={selectedState}
                label={t('state')}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                {availableStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {getLocalizedStateName(state)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', sm: '56px' } }}>
            <IconButton
              onClick={handleResetFilters}
              title={t('reset_filters')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                height: '40px',
                width: '100%',
              }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </Box>
        
      {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('error_occurred')}
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Please ensure the 'schemes' collection exists in Firestore with active schemes.
            </Typography>
          </Alert>
        )}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredSchemes && filteredSchemes.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {filteredSchemes?.map((scheme) => (
              <Card 
                key={scheme.id}
                elevation={0}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(103, 58, 183, 0.12)',
                    borderColor: 'primary.light',
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => navigate(`/scheme/${scheme.id}`)}
                  sx={{ p: { xs: 2, sm: 3 } }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h5" component="h3" color="text.primary" sx={{ flex: 1, pr: 2, fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}>
                        {getLocalizedTitle(scheme, currentLanguage)}
                      </Typography>
                      {scheme.isActive && (
                        <Chip 
                          label={t('active')} 
                          color="success" 
                          size="small"
                        />
                      )}
                    </Box>
                    
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {getLocalizedDescription(scheme, currentLanguage)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : (
          !error && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('no_schemes_found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('try_different_filters')}
              </Typography>
            </Box>
          )
        )}
      </Container>
  );
};